import { useEffect, useRef, useState } from "react";
import "../../../styles/panel/user/OwnerOtpStep.css";
// TODO: مسیر و اسم واقعی توابع API رو با چیزی که در api/storeRequest.js داری جایگزین کن
import { sendOwnerOtp, verifyOwnerOtp } from "../../../api/owner";

const RESEND_SECONDS = 60;
const OTP_LENGTH = 6;

function convertPersianToEnglish(str) {
  if (!str) return str;
  const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
  const arabicDigits = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
  return String(str)
    .replace(/[۰-۹]/g, (d) => persianDigits.indexOf(d).toString())
    .replace(/[٠-٩]/g, (d) => arabicDigits.indexOf(d).toString());
}

const isValidMobile = (m) => /^09\d{9}$/.test(convertPersianToEnglish(m || ""));

// اجازه فقط رقم (فارسی/عربی/انگلیسی) و حداکثر ۱۱ رقم بعد از تبدیل
const sanitizeMobileInput = (raw) => {
  const converted = convertPersianToEnglish(raw || "");
  const digitsOnly = converted.replace(/\D/g, "");
  return digitsOnly.slice(0, 11);
};

// owner: { mobile, verified, owner_id, fullName, nationalId, email }
// onVerified: بعد از تایید موفق OTP صدا زده می‌شود تا کاربر خودکار به مرحله بعد برود
export default function OwnerOtpStep({ owner, setOwner, onVerified }) {
  const [phase, setPhase] = useState(owner.verified ? "done" : "mobile"); // mobile | otp | done
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [seconds, setSeconds] = useState(0);
  const timerRef = useRef(null);
  const boxRefs = useRef([]);

  const code = digits.join("");

  useEffect(() => {
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    if (phase === "otp") {
      // فوکوس روی اولین خانه وقتی وارد مرحله otp میشیم
      boxRefs.current[0]?.focus();
    }
  }, [phase]);

  const startResendTimer = () => {
    setSeconds(RESEND_SECONDS);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async () => {
    const mobile = convertPersianToEnglish(owner.mobile);
    if (!isValidMobile(mobile)) {
      setError("شماره موبایل معتبر نیست");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await sendOwnerOtp(mobile);
      setDigits(Array(OTP_LENGTH).fill(""));
      setPhase("otp");
      startResendTimer();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "ارسال کد با خطا مواجه شد",
      );
    }
    setLoading(false);
  };

  const handleVerifyOtp = async (fullCode) => {
    const value = fullCode ?? code;
    if (value.length !== OTP_LENGTH) return;
    setError("");
    setLoading(true);
    try {
      const mobile = convertPersianToEnglish(owner.mobile);
      const res = await verifyOwnerOtp(mobile, convertPersianToEnglish(value));
      const data = res?.data || {};

      setOwner({
        ...owner,
        mobile,
        verified: true,
        owner_id: data.owner_id || null,
        fullName: data.full_name || owner.fullName || "",
        nationalId: data.national_id || owner.nationalId || "",
      });

      setPhase("done");
      onVerified && onVerified();
    } catch (err) {
      setError(
        err?.response?.data?.detail || err?.message || "کد وارد شده صحیح نیست",
      );
    }
    setLoading(false);
  };

  const handleEditMobile = () => {
    clearInterval(timerRef.current);
    setDigits(Array(OTP_LENGTH).fill(""));
    setError("");
    setPhase("mobile");
    setOwner({ ...owner, verified: false, owner_id: null });
  };

  const setDigitAt = (index, value) => {
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleBoxChange = (index, rawValue) => {
    const value = convertPersianToEnglish(rawValue);
    const onlyDigits = value.replace(/\D/g, "");

    if (!onlyDigits) {
      setDigitAt(index, "");
      return;
    }

    // پیست کردن یا تایپ سریع چند رقمی داخل یک خانه
    if (onlyDigits.length > 1) {
      setDigits((prev) => {
        const next = [...prev];
        for (let i = 0; i < onlyDigits.length && index + i < OTP_LENGTH; i++) {
          next[index + i] = onlyDigits[i];
        }
        return next;
      });
      const nextIndex = Math.min(index + onlyDigits.length, OTP_LENGTH - 1);
      boxRefs.current[nextIndex]?.focus();

      const merged = (() => {
        const next = [...digits];
        for (let i = 0; i < onlyDigits.length && index + i < OTP_LENGTH; i++) {
          next[index + i] = onlyDigits[i];
        }
        return next.join("");
      })();
      if (merged.length === OTP_LENGTH) handleVerifyOtp(merged);
      return;
    }

    setDigitAt(index, onlyDigits);
    if (index < OTP_LENGTH - 1) {
      boxRefs.current[index + 1]?.focus();
    }

    const merged = digits
      .map((d, i) => (i === index ? onlyDigits : d))
      .join("");
    if (merged.length === OTP_LENGTH) handleVerifyOtp(merged);
  };

  const handleBoxKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        setDigitAt(index, "");
      } else if (index > 0) {
        setDigitAt(index - 1, "");
        boxRefs.current[index - 1]?.focus();
      }
      e.preventDefault();
    } else if (e.key === "ArrowLeft" && index > 0) {
      boxRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      boxRefs.current[index + 1]?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (!loading && code.length === OTP_LENGTH) {
        handleVerifyOtp();
      }
    }
  };

  if (phase === "done") {
    return (
      <div className="owner-otp-step">
        <h3 className="request-step-title">اطلاعات مالک</h3>
        <p className="request-step-sub">شماره موبایل مالک تایید شد</p>

        <div className="otp-done-row">
          <span className="otp-done-check">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M5 13l4 4L19 7"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="otp-done-mobile">{owner.mobile}</span>
          {owner.fullName && (
            <span className="otp-done-name">{owner.fullName}</span>
          )}
          <button
            type="button"
            className="otp-link-btn otp-done-edit"
            onClick={handleEditMobile}
          >
            تغییر شماره
          </button>
        </div>

        <button
          type="button"
          className="request-btn primary otp-submit-btn otp-continue-btn"
          onClick={() => onVerified && onVerified()}
        >
          مرحله بعد
        </button>
      </div>
    );
  }

  return (
    <div className="owner-otp-step">
      <h3 className="request-step-title">اطلاعات مالک</h3>

      {phase === "mobile" && (
        <>
          <p className="request-step-sub">
            شماره موبایل مالک فروشگاه رو وارد کن تا کد تایید براش پیامک بشه
          </p>

          <div className="request-field otp-field">
            <label>شماره موبایل مالک</label>
            <input
              type="text"
              dir="ltr"
              inputMode="numeric"
              maxLength={11}
              value={owner.mobile}
              onChange={(e) =>
                setOwner({
                  ...owner,
                  mobile: sanitizeMobileInput(e.target.value),
                })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (!loading && isValidMobile(owner.mobile)) {
                    handleSendOtp();
                  }
                }
              }}
              placeholder="09xxxxxxxxx"
            />
          </div>

          <button
            type="button"
            className="request-btn primary otp-submit-btn"
            onClick={handleSendOtp}
            disabled={loading || !isValidMobile(owner.mobile)}
          >
            {loading ? "در حال ارسال..." : "ارسال کد تایید"}
          </button>
        </>
      )}

      {phase === "otp" && (
        <>
          <p className="request-step-sub">
            کد تایید به شماره {owner.mobile} پیامک شد
          </p>

          <div className="otp-boxes-owner" dir="ltr">
            {digits.map((digit, i) => (
              <input
                key={i}
                ref={(el) => (boxRefs.current[i] = el)}
                type="text"
                inputMode="numeric"
                maxLength={OTP_LENGTH}
                className="otp-box"
                value={digit}
                disabled={loading}
                onChange={(e) => handleBoxChange(i, e.target.value)}
                onKeyDown={(e) => handleBoxKeyDown(i, e)}
              />
            ))}
          </div>

          <div className="otp-hint">
            شماره اشتباهه؟{" "}
            <button
              type="button"
              className="otp-link-btn"
              onClick={handleEditMobile}
            >
              ویرایش شماره
            </button>
          </div>

          <button
            type="button"
            className="request-btn primary otp-submit-btn"
            onClick={() => handleVerifyOtp()}
            disabled={loading || code.length !== OTP_LENGTH}
          >
            {loading ? "در حال بررسی..." : "تایید کد"}
          </button>

          <button
            type="button"
            className="otp-link-btn otp-resend"
            onClick={handleSendOtp}
            disabled={loading || seconds > 0}
          >
            {seconds > 0
              ? `ارسال مجدد تا ${seconds} ثانیه دیگر`
              : "ارسال مجدد کد"}
          </button>
        </>
      )}

      {error && <div className="otp-error">{error}</div>}
    </div>
  );
}

export function isOwnerOtpStepValid(owner) {
  return !!owner.verified;
}
